/* ====================================================================
 *  migrations — одноразова міграція ключів кошика на формат
 *  productId|variantId (введений 2026-02-10, коміт 0daa492).
 *
 *  ВИДАЛИТИ, коли в проді не лишиться документів cartItems зі старим
 *  ключем. Орієнтир перевірки: не раніше 2026-12-01, підрахунком
 *  документів, де id !== productId|variantId.
 *
 *  Дешевий шлях: якщо всі позиції вже канонічні — виходимо без жодного
 *  читання варіантів і без записів. Якщо міграція нічого не змінює —
 *  теж не пишемо (раніше вона безумовно перезаписувала весь кошик на
 *  КОЖНОМУ логіні, бампаючи updatedAt і смикаючи снапшот).
 * ==================================================================== */

import {
  doc,
  serverTimestamp,
  writeBatch,
  type Firestore,
} from "firebase/firestore";

import {
  makeKey,
  canonicalKeyOf,
  toRemoteFields,
  sortByKey,
  type CartItem,
} from "./mapping";
import { cartCol, fetchProductVariants } from "./remote";

function normStr(v: unknown): string {
  return String(v || "")
    .trim()
    .toLowerCase();
}

function readField(obj: unknown, keys: string[]): string {
  if (!obj || typeof obj !== "object") return "";
  const o = obj as Record<string, unknown>;
  for (const k of keys) {
    const val = o[k];
    if (typeof val === "string" && val.trim()) return val;
    if (val && typeof val === "object") {
      const vo = val as Record<string, unknown>;
      const vv = vo.value || vo.label || vo.name;
      if (typeof vv === "string" && vv.trim()) return vv;
    }
  }
  return "";
}

/** Пошук варіанта за кольором/розміром — для позицій без variantId. */
function matchVariantIdByProps(
  variants: Record<string, unknown>[],
  color: string,
  size: string,
): string {
  const c = normStr(color);
  const s = normStr(size);
  if (!c && !s) return "";

  const hit = variants.find((v) => {
    const vColor = normStr(
      readField(v, ["color"]) ||
        readField(v?.color, ["value", "label"]) ||
        (typeof v?.color === "string" ? v.color : ""),
    );
    const vSize = normStr(String(v?.size || ""));

    if (c && s) return vColor === c && vSize === s;
    if (c) return vColor === c;
    if (s) return vSize === s;
    return false;
  });

  return hit?.id ? String(hit.id) : "";
}

/** Чи є взагалі кандидати на міграцію (без жодного I/O). */
export function needsMigration(items: CartItem[]): boolean {
  return (items || []).some(
    (it) => !it.variantId || it.key !== canonicalKeyOf(it),
  );
}

/**
 * Переносить позиції на канонічні ключі, зливаючи дублі.
 * Повертає новий список, якщо щось реально змінилось, інакше null
 * (записів у Firestore у цьому випадку не було).
 */
export async function migrateCartKeys(
  db: Firestore,
  uid: string,
  items: CartItem[],
): Promise<CartItem[] | null> {
  const list = (items || []).filter((r) => r.productId);
  if (!list.length) return null;
  if (!needsMigration(list)) return null;

  const variantsCache = new Map<string, Record<string, unknown>[]>();
  const getVariants = async (pid: string) => {
    if (!variantsCache.has(pid))
      variantsCache.set(pid, await fetchProductVariants(db, pid));
    return variantsCache.get(pid) as Record<string, unknown>[];
  };

  const merged = new Map<string, CartItem>();
  const toDeleteOld = new Set<string>();
  let changed = false;

  for (const r of list) {
    let variantId = r.variantId;
    if (!variantId) {
      const variants = await getVariants(r.productId);
      variantId = matchVariantIdByProps(variants, r.color, r.size);
      if (variantId) changed = true;
    }

    const newKey = makeKey({
      productId: r.productId,
      variantId,
      color: r.color,
      size: r.size,
    });
    const payload: CartItem = { ...r, key: newKey, variantId };

    const existing = merged.get(newKey);
    if (existing) {
      existing.qty = Number(existing.qty || 0) + Number(payload.qty || 0);
      if (!existing.variantId && payload.variantId)
        existing.variantId = payload.variantId;
      if (!existing.sku && payload.sku) existing.sku = payload.sku;
      changed = true;
    } else {
      merged.set(newKey, payload);
    }

    if (r.key !== newKey) {
      toDeleteOld.add(r.key);
      changed = true;
    }
  }

  if (!changed) return null;

  // set + delete мають поїхати однією атомарною пачкою
  const base = cartCol(db, uid);
  const batch = writeBatch(db);

  for (const [key, payload] of merged.entries()) {
    batch.set(
      doc(base, key),
      { ...toRemoteFields(payload), updatedAt: serverTimestamp() },
      { merge: true },
    );
  }
  for (const oldKey of toDeleteOld) {
    // не видаляємо ключ, який став канонічним для іншої позиції
    if (merged.has(oldKey)) continue;
    batch.delete(doc(base, oldKey));
  }

  await batch.commit();

  return sortByKey(Array.from(merged.values()));
}
